import React, { useState } from 'react';
import { CheckCircle, MapPin, DollarSign, Trash2 } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const BulkActions = ({ selectedCount, onBulkAction }) => {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCommuneModal, setShowCommuneModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newCommune, setNewCommune] = useState('');
  const [newPrice, setNewPrice] = useState('');

  const statusOptions = [
    { value: 'available', label: 'Disponible' },
    { value: 'reserved', label: 'Réservé' },
    { value: 'sold', label: 'Vendu' },
    { value: 'withdrawn', label: 'Retiré' }
  ];

  const handleStatusUpdate = () => {
    if (newStatus) {
      onBulkAction?.('status', { status: newStatus });
      setShowStatusModal(false);
      setNewStatus('');
    }
  };

  const handleCommuneUpdate = () => {
    if (newCommune) {
      onBulkAction?.('commune', { communeId: newCommune });
      setShowCommuneModal(false);
      setNewCommune('');
    }
  };

  const handlePriceUpdate = () => {
    if (newPrice && !isNaN(newPrice)) {
      onBulkAction?.('price', { price: Number(newPrice) });
      setShowPriceModal(false);
      setNewPrice('');
    }
  };

  return (
    <>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-blue-600 mr-2" />
            <span className="text-blue-900 font-medium">
              {selectedCount} propriété{selectedCount > 1 ? 's' : ''} sélectionnée{selectedCount > 1 ? 's' : ''}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowStatusModal(true)}
              className="flex items-center gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              Changer le statut
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCommuneModal(true)}
              className="flex items-center gap-2"
            >
              <MapPin className="h-4 w-4" />
              Modifier commune
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPriceModal(true)}
              className="flex items-center gap-2"
            >
              <DollarSign className="h-4 w-4" />
              Ajuster prix
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => onBulkAction?.('delete')}
              className="flex items-center gap-2 text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </Button>
          </div>
        </div>
      </div>
      {/* Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Modifier le statut des propriétés
            </h3>
            <p className="text-gray-600 mb-4">
              Changer le statut de {selectedCount} propriété{selectedCount > 1 ? 's' : ''}
            </p>
            
            <Select
              value={newStatus}
              onValueChange={setNewStatus}
              placeholder="Sélectionner un nouveau statut"
              className="mb-4"
            >
              {statusOptions?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </Select>
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowStatusModal(false)}
              >
                Annuler
              </Button>
              <Button
                onClick={handleStatusUpdate}
                disabled={!newStatus}
              >
                Confirmer
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Commune Modal */}
      {showCommuneModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Modifier la commune des propriétés
            </h3>
            <p className="text-gray-600 mb-4">
              Changer la commune de {selectedCount} propriété{selectedCount > 1 ? 's' : ''}
            </p>
            
            <Input
              placeholder="ID de la nouvelle commune"
              value={newCommune}
              onChange={(e) => setNewCommune(e?.target?.value)}
              className="mb-4"
            />
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowCommuneModal(false)}
              >
                Annuler
              </Button>
              <Button
                onClick={handleCommuneUpdate}
                disabled={!newCommune}
              >
                Confirmer
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Price Modal */}
      {showPriceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Modifier le prix des propriétés
            </h3>
            <p className="text-gray-600 mb-4">
              Définir un nouveau prix pour {selectedCount} propriété{selectedCount > 1 ? 's' : ''}
            </p>
            
            <Input
              type="number"
              placeholder="Nouveau prix en XOF"
              value={newPrice}
              onChange={(e) => setNewPrice(e?.target?.value)}
              className="mb-4"
            />
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowPriceModal(false)}
              >
                Annuler
              </Button>
              <Button
                onClick={handlePriceUpdate}
                disabled={!newPrice || isNaN(newPrice)}
              >
                Confirmer
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkActions;